import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { Entitlements, ExportFormat } from '../domains/access/models/Entitlements';

interface EntitlementsResponse {
  success: boolean;
  data?: Entitlements;
  code?: string;
  error?: string;
}

export function useEntitlements(diagramId?: string) {
  const { isAuthenticated } = useAuthStore();

  const {
    data: entitlements,
    isLoading: loading,
    error,
    refetch
  } = useQuery<Entitlements>({
    queryKey: ['entitlements', diagramId ?? null],
    enabled: isAuthenticated,
    queryFn: async () => {
      const query = diagramId ? `?diagramId=${encodeURIComponent(diagramId)}` : '';
      const response = await fetch(`/api/me/entitlements${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data: EntitlementsResponse = await response.json();

      if (data.success && data.data) {
        return data.data;
      } else {
        throw new Error(data.error || 'Failed to fetch entitlements');
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - server-side cache is 5 minutes, so client can be longer
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Helper functions for common capability checks
  const canExportFormat = useCallback((format: ExportFormat) => {
    return entitlements?.capabilities.export.allowedFormats.includes(format) ?? false;
  }, [entitlements]);

  const canShareWithGuests = useCallback(() => {
    return entitlements?.capabilities.sharing.mode === 'fine_grained';
  }, [entitlements]);

  const canInviteByEmail = useCallback(() => {
    return entitlements?.capabilities.sharing.allowPerEmailInvites ?? false;
  }, [entitlements]);

  const getShapeLimit = useCallback(() => {
    return entitlements?.capabilities?.maxShapesPerDiagram || null;
  }, [entitlements]);

  return {
    entitlements,
    loading,
    error: error?.message || null,
    refetch,
    // Helper functions
    canExportFormat,
    canShareWithGuests,
    canInviteByEmail,
    getShapeLimit,
  };
}
